import { assert } from '../../../lib/assert-sidecar'
import { l } from '../../../lib/imports'
import { createId } from '../../../main/common'
import { voyageBodies } from '../voyageBodies-sidecar'
import { voyageCarryable } from '../voyageCarryable-sidecar'
import { EVoyageCarryableType } from '../voyageCarryableData'
import { voyageCrew } from '../voyageCrew-sidecar'
import { voyageTilemap } from '../voyageTilemap-sidecar'
import { voyageWebsockets } from '../voyageWebsockets-sidecar'
import { EVoyageMessage } from './voyageMessageIds'
import type { IMessageWriter } from '../../../engine/messageWriter'
import { messageWriter } from '../../../engine/messageWriter-sidecar'
import { websockets } from '../../../engine/websockets-sidecar'

export interface IMessageCrewCarry {
  command: EVoyageCrewCarryCommand
  type: EVoyageCarryableType
  crewId: number
  carryId1: number
  carryId2: number
  i: number
  j: number
}

export enum EVoyageCrewCarryCommand {
  Create = 0,
  Destroy = 1,
  Pickup = 2,
  Drop = 3,
  Swap = 4,
}

export const voyageMessageId = EVoyageMessage.CrewCarry

export function write(m: IMessageWriter, d: IMessageCrewCarry) {
  messageWriter.write8(m, voyageMessageId)
  messageWriter.write8(m, d.command)
  messageWriter.write8(m, d.type)
  messageWriter.write32(m, d.crewId)
  messageWriter.write32(m, d.carryId1)
  messageWriter.write32(m, d.carryId2)
  messageWriter.write16(m, d.i)
  messageWriter.write16(m, d.j)
}

export function read(m: IMessageWriter) {
  let t = messageWriter.read8(m)
  assert.eq(t, voyageMessageId)

  let d: IMessageCrewCarry = {
    command: messageWriter.read8(m),
    type: messageWriter.read8(m),
    crewId: messageWriter.read32(m),
    carryId1: messageWriter.read32(m),
    carryId2: messageWriter.read32(m),
    i: messageWriter.read16(m),
    j: messageWriter.read16(m),
  }

  return d
}

export function test() {
  let ex: IMessageCrewCarry = {
    crewId: 100,
    type: EVoyageCarryableType.Coffee,
    command: EVoyageCrewCarryCommand.Create,
    carryId1: 123,
    carryId2: 456,
    i: 7,
    j: 8,
  }
  messageWriter.test('crew-carry', ex, write, read)
}

export function process(m: IMessageWriter) {
  let msg = read(m)

  if (msg.command === EVoyageCrewCarryCommand.Create) {
    let cy = voyageCarryable.create(msg.type, msg.carryId1)
    if (msg.crewId) {
      let cr = voyageCrew.findByIdSafe(msg.crewId)
      if (!cr) {
        websockets.reportBadCommand(
          m,
          msg,
          'voyageMessage-crew-carry',
          'crewId',
        )
        return
      }

      if (msg.type === EVoyageCarryableType.CrewCorpse) {
        if (cr.crewState && cr.crewState.body) {
          voyageBodies.rotate(cr.crewState.body)
          voyageCarryable.setBody(cy, cr.crewState.body)
          cr.crewState.body = null
          voyageCarryable.setAtTile(cy, cr.tile)
          voyageCrew.kill(cr)
        }
      } else {
        voyageCarryable.setAtCrew(cy, cr)
      }
    } else {
      let t = voyageTilemap.getSafe(msg.i, msg.j)
      assert.notNil(t)
      voyageCarryable.setAtTile(cy, t)
    }
  }
  if (msg.command === EVoyageCrewCarryCommand.Destroy) {
    let cy = voyageCarryable.findById(msg.carryId1)
    assert.notNil(cy)
    voyageCarryable.kill(cy)
  }

  if (msg.command === EVoyageCrewCarryCommand.Pickup) {
    let cy = voyageCarryable.findById(msg.carryId1)
    assert.notNil(cy)

    let cr = voyageCrew.findById(msg.crewId)
    assert.notNil(cr)

    voyageCarryable.setAtCrew(cy, cr)
  }
  if (msg.command === EVoyageCrewCarryCommand.Drop) {
    let cy = voyageCarryable.findById(msg.carryId1)
    assert.notNil(cy)

    let t = voyageTilemap.getSafe(msg.i, msg.j)
    assert.notNil(t)
    voyageCarryable.setAtTile(cy, t)
  }

  if (msg.command === EVoyageCrewCarryCommand.Swap) {
    let cy1 = voyageCarryable.findById(msg.carryId1)
    assert.notNil(cy1)
    let cy2 = voyageCarryable.findById(msg.carryId2)
    assert.notNil(cy2)

    let t = voyageTilemap.getSafe(msg.i, msg.j)
    assert.notNil(t)

    let cr = voyageCrew.findById(msg.crewId)
    assert.notNil(cr)

    voyageCarryable.setAtTile(cy1, t)
    voyageCarryable.setAtCrew(cy2, cr)
  }

  //   if (msg.type === EVoyageCarryableType.BattleRifle) {
  //     let t = voyageTilemap.getSafe(msg.i, msg.j)
  //     if (t && t.system && t.system.data) {
  //       t.system.ammo++
  //     }
  //   }
  // }
}

export function sendCreateMessageAndImmediatelyExecute(params: {
  type: EVoyageCarryableType
  i: number
  j: number
  crewId?: number
}) {
  assert.t(voyageWebsockets.getIsHost())
  let id = createId()
  sendMessageAndImmediatelyExecute({
    type: params.type,
    carryId1: id,
    carryId2: 0,
    command: EVoyageCrewCarryCommand.Create,
    crewId: params.crewId || 0,
    i: params.i,
    j: params.j,
  })
  return id
}

export function sendDestroyMessageAndImmediatelyExecute(params: {
  id: number
}) {
  assert.t(voyageWebsockets.getIsHost())
  sendMessageAndImmediatelyExecute({
    type: EVoyageCarryableType.Null,
    carryId1: params.id,
    carryId2: 0,
    command: EVoyageCrewCarryCommand.Destroy,
    crewId: 0,
    i: 0,
    j: 0,
  })
}

export function sendMessageAndImmediatelyExecute(msg: IMessageCrewCarry) {
  websockets._sendMessageAndImmediatelyExecute(write, msg)
}
